import { Link } from 'react-router-dom';
import { AlarmClock, FileSignature, FileText, UserCheck } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { fechaCorta, textoDias, type TipoVencimiento, type Vencimiento } from '@/lib/personal';

/**
 * Lo que está a punto de vencer, o ya venció.
 *
 * Es una lista y no un calendario a propósito: la pregunta es «¿qué tengo que
 * hacer?», no «¿qué día es?». Lo mismo que manda el barrido diario a Telegram,
 * aquí para mirarlo cuando se quiera.
 *
 * Un contrato ya vencido en alguien que sigue de alta encabeza la lista: o se
 * le hizo indefinido y nadie lo apuntó, o la ficha está mal. Las dos cosas hay
 * que arreglarlas hoy.
 */
const ICONO: Record<TipoVencimiento, any> = {
  contrato: FileSignature,
  periodo_prueba: UserCheck,
  documento: FileText,
};

const ETIQUETA: Record<TipoVencimiento, string> = {
  contrato: 'Contrato',
  periodo_prueba: 'Periodo de prueba',
  documento: 'Documento',
};

export default function PersonalVencimientosPage() {
  const { data, loading, error } = useApi<{ vencimientos: Vencimiento[]; total: number }>(
    () => api.getVencimientos(),
    [],
  );

  if (loading) return <LoadingSpinner />;
  if (error) return <EmptyState title="No se han podido cargar los vencimientos" description={error} />;

  const lista = data?.vencimientos || [];
  if (lista.length === 0) {
    return (
      <EmptyState
        title="Nada a la vista"
        description="Ningún contrato, periodo de prueba ni documento vence en las próximas semanas."
      />
    );
  }

  const vencidos = lista.filter((v) => v.dias < 0);
  const proximos = lista.filter((v) => v.dias >= 0);

  return (
    <div className="space-y-6">
      {vencidos.length > 0 && (
        <Grupo titulo="Ya han vencido" tono="red" items={vencidos} />
      )}
      {proximos.length > 0 && (
        <Grupo titulo="Próximos" tono="normal" items={proximos} />
      )}
      <p className="text-[11px] text-sereno-dark/35">
        Se avisa de los contratos con 60 días, de los periodos de prueba con 15 y de los documentos con 90.
        Cada aviso genera además una tarea, una sola vez.
      </p>
    </div>
  );
}

function Grupo({ titulo, tono, items }: { titulo: string; tono: 'red' | 'normal'; items: Vencimiento[] }) {
  return (
    <div>
      <h2 className={`text-sm font-semibold uppercase tracking-wider mb-2 ${tono === 'red' ? 'text-red-700' : 'text-sereno-dark/60'}`}>
        {titulo} <span className="font-normal">({items.length})</span>
      </h2>
      <div className="card p-0 overflow-hidden divide-y divide-gray-50">
        {items.map((v) => {
          const Icono = ICONO[v.tipo];
          const urgente = v.dias <= 7;
          return (
            <Link
              key={v.clave}
              to={`/equipo/personal/${v.empleadoId}?tab=${v.tipo === 'documento' ? 'documentos' : 'contrato'}`}
              className="flex items-center gap-3 px-4 sm:px-6 py-3.5 hover:bg-sereno-blue/20 transition-colors"
            >
              <div
                className={`p-1.5 rounded-lg flex-shrink-0 ${
                  v.dias < 0 ? 'bg-red-100 text-red-600' : urgente ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-sereno-dark/40'
                }`}
              >
                <Icono size={15} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{v.empleadoNombre}</p>
                <p className="text-xs text-sereno-dark/50 truncate">
                  {ETIQUETA[v.tipo]} · {v.detalle}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className={`text-sm font-medium ${v.dias < 0 ? 'text-red-600' : urgente ? 'text-amber-600' : ''}`}>
                  {v.dias < 0 ? `Venció ${textoDias(v.dias)}` : `Vence ${textoDias(v.dias)}`}
                </p>
                <p className="text-xs text-sereno-dark/40">{fechaCorta(v.fecha)}</p>
              </div>
              <AlarmClock size={14} className="text-sereno-dark/20 flex-shrink-0 hidden sm:block" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}
